/**
 * What a work page hands to the Agent's bar.
 *
 * A work page has a few small things that belong to the Agent rather than to
 * the page: load it again, the way back to the conversation it came from, the
 * places its plugin points at, what the Agent last changed in it. The bar draws
 * them in its 更多 menu, so the page itself keeps nothing above its content.
 *
 * A page registers what it has while it is on screen and takes it back when it
 * goes (`useWorkTool`). With no bar above -- a test, a page inside a dialog or a
 * card -- there is nobody to hand them to and the page draws them where it
 * always did.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/** Load the page again. `busy` while it is loading; `disabled` while that would lose a press that is still waiting. */
export interface ReloadTool { run: () => unknown; busy: boolean; disabled: boolean }
export interface BackTool { label: string; href: string }
export interface LinksTool { label: string; items: { label: string; href: string }[] }
/** What the Agent last changed in this page, in the person's own words, and where to see how. */
export interface ChangeTool { agent: string; task: string; trace?: string }

/** The pages of the work the Agent's bar is open on, drawn as its row of tabs. */
export interface PagesTool { label: string; items: { label: string; href: string; active: boolean }[] }

export interface WorkTools {
  pages?: PagesTool;
  reload?: ReloadTool;
  back?: BackTool;
  links?: LinksTool;
  change?: ChangeTool;
}

type Setter = <K extends keyof WorkTools>(key: K, value: WorkTools[K] | undefined, owner: object) => void;
const Registry = createContext<Setter | null>(null);

/** The bar's side: the tools held now, and the provider that takes them. */
export function useWorkToolsState() {
  const [tools, setTools] = useState<WorkTools>({});
  // Who put each one there, so a page that goes does not take away a tool another page now holds.
  const owners = useRef<Partial<Record<keyof WorkTools, object>>>({});
  const set = useCallback<Setter>((key, value, owner) => {
    if (value === undefined) {
      if (owners.current[key] !== owner) return;
      delete owners.current[key];
      setTools(held => { const { [key]: _gone, ...rest } = held; return rest; });
      return;
    }
    owners.current[key] = owner;
    setTools(held => ({ ...held, [key]: value }));
  }, []);
  return { tools, set };
}

export const WorkToolsProvider = Registry.Provider;

/** Whether a bar is listening: a page that draws its own tools asks this first. */
export function useHasWorkBar(): boolean {
  return useContext(Registry) !== null;
}

/**
 * Hand one tool to the bar for as long as `value` is given and the page is mounted.
 * `wanted` false (the page is not the one the bar speaks for) registers nothing.
 */
export function useWorkTool<K extends keyof WorkTools>(key: K, value: WorkTools[K] | undefined, wanted: boolean) {
  const set = useContext(Registry);
  const owner = useMemo(() => ({}), []);
  const given = wanted && set !== null ? value : undefined;
  // The value is a fresh object every render; what it says is what decides whether the bar re-reads it.
  const said = JSON.stringify(given ?? null, (_name, part) => typeof part === "function" ? "()" : part);
  const latest = useRef(given);
  latest.current = given;
  useEffect(() => {
    set?.(key, latest.current, owner);
  }, [set, key, owner, said]);
  useEffect(() => () => set?.(key, undefined, owner), [set, key, owner]);
}
